// Define the expected constructor parameter structure
interface AuthErrorParams {
  code: string;
  message: string;
  httpStatus: number;
}

export class AuthenticationError extends Error {
  public readonly code: string;
  public readonly httpStatus: number;

  constructor({ code, message, httpStatus }: AuthErrorParams) {
    super(message);
    this.name = 'AuthenticationError';
    this.code = code;
    this.httpStatus = httpStatus;
  }

  static invalidCredentials(): AuthenticationError {
    return new AuthenticationError({
      code: 'INVALID_CREDENTIALS',
      message: 'Invalid email or password',
      httpStatus: 401,
    });
  }

  static userNotFound(): AuthenticationError {
    return new AuthenticationError({
      code: 'USER_NOT_FOUND',
      message: 'User not found',
      httpStatus: 404,
    });
  }

  static userNotConfirmed(): AuthenticationError {
    return new AuthenticationError({
      code: 'USER_NOT_CONFIRMED',
      message: 'User not confirmed',
      httpStatus: 403,
    });
  }

  static sessionExpired(): AuthenticationError {
    return new AuthenticationError({
      code: 'SESSION_EXPIRED',
      message: 'Session has expired',
      httpStatus: 401,
    });
  }

  static tokenInvalid(): AuthenticationError {
    return new AuthenticationError({
      code: 'TOKEN_INVALID',
      message: 'Invalid or expired token',
      httpStatus: 401,
    });
  }

  static userSuspended(): AuthenticationError {
    return new AuthenticationError({
      code: 'USER_SUSPENDED',
      message: 'User account is suspended',
      httpStatus: 403,
    });
  }

  static loginFailed(): AuthenticationError {
    return new AuthenticationError({
      code: 'LOGIN_FAILED',
      message: 'Login failed',
      httpStatus: 401,
    });
  }

  static tooManyAttempts(): AuthenticationError {
    return new AuthenticationError({
      code: 'TOO_MANY_ATTEMPTS',
      message: 'Too many attempts, please try again later',
      httpStatus: 429,
    });
  }
  static registrationFailed(): AuthenticationError {
    return new AuthenticationError({
      code: 'REGISTRATION_FAILED',
      message: 'Failed to register user',
      httpStatus: 400,
    });
  }

  static confirmationFailed(): AuthenticationError {
    return new AuthenticationError({
      code: 'CONFIRMATION_FAILED',
      message: 'Failed to confirm user',
      httpStatus: 400,
    });
  }

  static invalidToken(): AuthenticationError {
    return new AuthenticationError({
      code: 'INVALID_TOKEN',
      message: 'Invalid token provided',
      httpStatus: 401,
    });
  }

  static userExists(): AuthenticationError {
    return new AuthenticationError({
      code: 'USER_EXISTS',
      message: 'User already exists',
      httpStatus: 409,
    });
  }
}
